import {
	Stack,
	Typography,
	Unstable_Grid2 as Grid,
	TextField,
	Button,
	styled,
	SvgIcon,
	SelectChangeEvent,
} from "@mui/material";
import { useState, useCallback, type FormEvent, type ChangeEvent, useEffect } from "react";
import ArrowUpTrayIcon from "@heroicons/react/24/solid/ArrowUpTrayIcon";
import type { ILink } from "../../entities/link.entity";
import { useMutation } from "@tanstack/react-query";
import { useCategoriesList } from "../hook/useCategoriesList";
import {
	createLink,
	firebaseAddStorage,
	updateLink,
} from "../../utils/firebase";
import { enqueueSnackbar } from "notistack";
import { captureException } from "@sentry/react";
import { SubCategoryPathInput } from "./SubCategoryPathInput";
import { GroupInput } from "./GroupInput";

interface LinkProps {
	item?: ILink;
	onLinkSaved: () => void;
}

export default function LinkDetail(props: LinkProps) {
	const { item, onLinkSaved } = props;
	const categoriesQuery = useCategoriesList();

	const saveLinkMutation = useMutation({
		mutationKey: ["updateLink"],
		async mutationFn(data: ILink) {
			if (data.image?.file) {
				const response = await firebaseAddStorage(
					"links",
					data.image.file,
				);

				data.image = response;
				// biome-ignore lint/performance/noDelete: <explanation>
				delete data.image.file;
			}

			// Remove undefined image field if it exists
			if (data.image === undefined) {
				// biome-ignore lint/performance/noDelete: <explanation>
				delete data.image;
			}

			if (data.id) {
				await updateLink(data);
			} else {
				await createLink(data);
			}
		},
		onSuccess() {
			onLinkSaved();
			categoriesQuery.refetch();
			enqueueSnackbar({
				variant: "success",
				message: "Link salvo com sucesso",
			});
		},
		onError: (error) => {
			captureException(error);
			enqueueSnackbar({
				variant: "error",
				message: "Erro ao salvar link",
			});
		},
	});

	const handleSubmitLink = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			saveLinkMutation.mutate(values);
		},
		[saveLinkMutation],
	);

	const [values, setValues] = useState<ILink>(
		item || {
			id: "",
			label: "",
			url: "",
			subCategoryPaths: [],
			groupIds: [],
			createdAt: new Date(),
			image: undefined,
		},
	);

	// Update values when item changes (for editing)
	useEffect(() => {
		if (item) {
			setValues({
				...item,
				// Ensure backward compatibility - initialize subCategoryPaths if missing
				subCategoryPaths: item.subCategoryPaths || [],
			});
		}
	}, [item]);

	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		console.log("new file", event.target.files);
		const file = event.target.files?.[0];

		if (file) {
			const imageUrl = URL.createObjectURL(file);
			setValues({ ...values, image: { url: imageUrl, file } });
		}
	};

	const VisuallyHiddenInput = styled("input")`
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    bottom: 0;
    left: 0;
    white-space: nowrap;
    width: 1px;
  `;

	const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setValues((prevState) => ({
			...prevState,
			[event.target.name]: event.target.value,
		}));
	}, []);

	const handleSelectSubCategoryPaths = useCallback((event: SelectChangeEvent<string[]>) => {
		setValues((prevState) => ({
			...prevState,
			subCategoryPaths: event.target.value as string[],
		}));
	}, []);

	const handleSelectGroups = useCallback((event: SelectChangeEvent<string[]>) => {
		setValues((prevState) => ({
			...prevState,
			groupIds: event.target.value as string[],
		}));
	}, []);

	return (
		<Stack spacing={3}>
			<div>
				<Typography variant="h4">Link</Typography>
			</div>

			<form autoComplete="off" noValidate onSubmit={handleSubmitLink}>
				<Grid container spacing={3}>
					<Grid xs={12} md={12}>
						<TextField
							fullWidth
							helperText="Digite o label do link"
							label="Label"
							name="label"
							onChange={handleChange}
							required
							value={values.label}
						/>
					</Grid>
					<Grid xs={12} md={12}>
						<TextField
							fullWidth
							helperText="Digite a URL do link"
							label="URL"
							name="url"
							onChange={handleChange}
							required
							value={values.url}
						/>
					</Grid>
					<Grid xs={12} md={6}>	
						<SubCategoryPathInput
							value={values.subCategoryPaths || []}
							onChange={handleSelectSubCategoryPaths}
						/>
					</Grid>
					<Grid xs={12} md={6}>
						<GroupInput
							value={values.groupIds}
							onChange={handleSelectGroups}
						/>
					</Grid>
					<Grid sx={{ gap: "10px" }} xs={12}>
						<Stack spacing={2}>
							{values.image?.url && (
								<img
									style={{ borderRadius: 5, maxWidth: "100%", height: "auto" }}
									src={values.image.url}
									alt="Imagem do link"
								/>
							)}
							<Button
								component="label"
								variant="outlined"
								startIcon={
									<SvgIcon fontSize="small">
										<ArrowUpTrayIcon />
									</SvgIcon>
								}
								href="#file-upload"
							>
								SUBIR IMAGEM (OPCIONAL)
								<VisuallyHiddenInput
									id="file-upload"
									type="file"
									accept="image/jpg, image/jpeg, image/png, image/webp, image/gif, image/svg+xml, image/bmp"
									onChange={handleImageUpload}
								/>
							</Button>
						</Stack>
					</Grid>
					<Grid xs={12}>
						<Button
							type="submit"
							sx={{ marginTop: 5 }}
							variant="contained"
							fullWidth
							disabled={saveLinkMutation.isLoading}
						>
							{saveLinkMutation.isLoading ? "Salvando..." : "Salvar"}
						</Button>
					</Grid>
				</Grid>
			</form>
		</Stack>
	);
}
